import React, { useEffect, useState } from 'react';
import { TotalPricingRowProps } from './closeoutTypes';
import { formatCurrency, formatNumber } from './closeoutHelpers';
import {
  useGetTotalFinalPriceChangeOrderCloseouts,
  useGetTotalFinalPriceCloseouts,
  useUpdateTotalFinalPriceChangeOrderCloseoutMutation,
  useUpdateTotalFinalPriceCloseoutMutation,
} from '../../hooks/useCloseoutHooks';
import { TableCell, TableRow, TextField, useTheme, alpha } from '@mui/material';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useLockBid } from '../../utils/useLockBid';

const cloeoutNameMap = {
  extendestimate: 'Extended Cost',
  directlaborcloseout: 'Direct Labor',
  laborfactoringcloseout: 'Labor Factoring',
  incidentallaborcloseout: 'Incidental Labor',
  indirectlaborcloseout: 'Indirect Labor',
  equipmentcloseout: 'Equipment',
  generalexpensecloseout: 'General Expense',
  subcontractcloseout: 'Subcontracts',
  quotecloseout: 'Quotes',
};

export const TotalPricingRow: React.FC<TotalPricingRowProps> = ({
  closeoutName,
  closeout,
  rowData,
  hideColumns = [],
}) => {
  const theme = useTheme();
  const { lockBid } = useLockBid();
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const { mutate: updateEstimateCloseout } = useUpdateTotalFinalPriceCloseoutMutation();
  const { isFetching: isFetchingEstimate, refetch: refetchEstimateCloseout } =
    useGetTotalFinalPriceCloseouts();

  const { mutate: updateChangeOrderCloseout } =
    useUpdateTotalFinalPriceChangeOrderCloseoutMutation();
  const { isFetching: isFetchingChangeOrder, refetch: refetchChangeOrder } =
    useGetTotalFinalPriceChangeOrderCloseouts();

  const updateCloseout = selectedEstimate?.estimateid
    ? updateEstimateCloseout
    : updateChangeOrderCloseout;

  const refetch = selectedEstimate?.estimateid ? refetchEstimateCloseout : refetchChangeOrder;

  const isFetching = selectedEstimate?.estimateid ? isFetchingEstimate : isFetchingChangeOrder;

  // Calculate row cost from list
  const rowCost = closeout
    ? (closeout as any)[`${closeoutName}List`]?.reduce(
        (acc: number, item: any) => acc + (item.totalcost || 0),
        0
      ) || 0
    : 0;

  // Extract values
  const adjustmentValue = closeout?.adjustment ?? 0;
  const overheadValue = closeout?.overhead ?? 0;
  const marginValue = closeout?.margin ?? 0;

  // Local state for input fields
  const [adjustedCost, setAdjustedCost] = useState(formatCurrency(adjustmentValue));
  const [overhead, setOverhead] = useState(formatCurrency(overheadValue));
  const [totalMarkup, setTotalMarkup] = useState(formatNumber(marginValue));

  // Track last known values to prevent unnecessary updates
  const [lastAdjustment, setLastAdjustment] = useState(adjustmentValue);
  const [lastOverhead, setLastOverhead] = useState(overheadValue);
  const [lastMargin, setLastMargin] = useState(marginValue);

  // Sync with props when external data updates
  useEffect(() => {
    setAdjustedCost(formatNumber(adjustmentValue));
    setOverhead(formatNumber(overheadValue));
    setTotalMarkup(formatNumber(marginValue));

    setLastAdjustment(adjustmentValue);
    setLastOverhead(overheadValue);
    setLastMargin(marginValue);
  }, [adjustmentValue, overheadValue, marginValue]);

  type Field = 'adjustment' | 'overhead' | 'margin';

  // Handle user input
  const handleChange =
    (field: Field, setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isFetching) return;
      const value = e.target.value;
      if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
        setter(value);
      }
    };

  // Optimistic update on blur, **only if the value changed**
  const handleBlur = async (field: Field, value: string) => {
    const numericValue = parseFloat(value) || 0;

    if (
      (field === 'adjustment' && numericValue === lastAdjustment) ||
      (field === 'overhead' && numericValue === lastOverhead) ||
      (field === 'margin' && numericValue === lastMargin)
    ) {
      return; // **Avoid unnecessary updates if nothing changed**
    }

    // Update local state optimistically
    if (field === 'adjustment') {
      setAdjustedCost(numericValue.toFixed(2));
      setLastAdjustment(numericValue);
    }
    if (field === 'overhead') {
      setOverhead(numericValue.toFixed(2));
      setLastOverhead(numericValue);
    }
    if (field === 'margin') {
      setTotalMarkup(numericValue.toFixed(2));
      setLastMargin(numericValue);
    }

    const updatedCloseout = {
      ...rowData,
      [closeoutName]: {
        ...(rowData[closeoutName] || {}),
        [field]: numericValue,
      },
    };

    updateCloseout(updatedCloseout, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  // Common styles
  const rowStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.common.black, 0.04),
    },
    '&:nth-of-type(even)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.common.black, 0.02),
    },
  };

  const cellStyle = {
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap' as const,
  };

  const inputCellStyle = {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.5)
          : theme.palette.background.paper,
      '& fieldset': {
        borderColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.2)
            : theme.palette.grey[300],
      },
      '&:hover fieldset': {
        borderColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.3)
            : theme.palette.grey[400],
      },
      '&.Mui-focused fieldset': {
        borderColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.5)
            : theme.palette.grey[500],
        borderWidth: 1,
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontSize: '0.875rem',
      padding: theme.spacing(1),
      height: '1.4375em',
      width: '100px',
    },
  };

  const shouldShowColumn = (column: string) => {
    return !hideColumns.includes(column);
  };

  return (
    <TableRow
      hover
      sx={rowStyle}
      role="row"
      aria-label={`${cloeoutNameMap[closeoutName]} pricing row`}
    >
      <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>{cloeoutNameMap[closeoutName]}</TableCell>
      <TableCell sx={cellStyle}>{formatCurrency(rowCost)}</TableCell>
      <TableCell sx={inputCellStyle}>
        <TextField
          type="text"
          value={adjustedCost}
          inputProps={{
            min: 0,
            max: 99999999.99,
            step: '0.01',
            'aria-label': `Adjustment value for ${cloeoutNameMap[closeoutName]}`,
          }}
          onChange={handleChange('adjustment', setAdjustedCost)}
          onBlur={e => handleBlur('adjustment', e.target.value)}
          size="small"
          variant="outlined"
          sx={textFieldStyle}
          disabled={lockBid()}
        />
      </TableCell>
      <TableCell sx={cellStyle}>{formatCurrency(closeout?.adjustedcost || 0)}</TableCell>
      <TableCell sx={inputCellStyle}>
        <TextField
          type="text"
          value={overhead}
          inputProps={{
            min: 0,
            max: 99999999.99,
            step: '0.01',
            'aria-label': `Overhead value for ${cloeoutNameMap[closeoutName]}`,
          }}
          onChange={handleChange('overhead', setOverhead)}
          onBlur={e => handleBlur('overhead', e.target.value)}
          size="small"
          variant="outlined"
          sx={textFieldStyle}
          disabled={lockBid()}
        />
      </TableCell>
      <TableCell sx={cellStyle}>{formatCurrency(closeout?.overheadcost || 0)}</TableCell>
      <TableCell sx={inputCellStyle}>
        <TextField
          type="text"
          value={totalMarkup}
          inputProps={{
            min: 0,
            max: 99999999.99,
            step: '0.01',
            'aria-label': `Profit margin for ${cloeoutNameMap[closeoutName]}`,
          }}
          onChange={handleChange('margin', setTotalMarkup)}
          onBlur={e => handleBlur('margin', e.target.value)}
          size="small"
          variant="outlined"
          sx={textFieldStyle}
          disabled={lockBid()}
        />
      </TableCell>

      {shouldShowColumn('profit') && (
        <TableCell sx={cellStyle}>{formatCurrency(closeout?.profitcost || 0)}</TableCell>
      )}

      {shouldShowColumn('totalCost') && (
        <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>
          {formatCurrency(closeout?.totalcost || 0)}
        </TableCell>
      )}
    </TableRow>
  );
};
